// src/components/ProtectedRoute.jsx
import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ProtectedRoute = ({ children, isAdminRoute = false }) => {
    const { isAuthenticated, loading, currentUser } = useAuth();
    const location = useLocation();

    // Keep logs active for this test
    console.log('[ProtectedRoute] Rendering:', {
        pathname: location.pathname,
        isAdminRoute,
        loading,
        isAuthenticated,
        currentUserRole: currentUser?.role
    });

    // 1. If the initial authentication check is still loading, display loading.
    //    AuthContext sets loading to true initially and false when checkAuthStatus completes.
    if (loading) {
        console.log('[ProtectedRoute] Condition: loading=true. Rendering loading indicator.');
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontFamily: 'var(--font-secondary)',
                color: 'var(--text-dark-heading)',
                backgroundColor: 'var(--bg-light)'
            }}>
                Loading authentication status...
            </div>
        );
    }

    // 2. If loading is finished AND the user is definitively NOT authenticated, redirect.
    //    This check now happens ONLY after loading is confirmed false.
    if (!loading && !isAuthenticated) {
        console.log('[ProtectedRoute] Condition: loading=false, isAuthenticated=false. Redirecting to login.');
        return <Navigate
            to="/login"
            state={{
                from: location,
                message: location.state?.message || "You must be logged in to access this page."
            }}
            replace
        />;
    }

    // 3. If loading is finished AND user IS authenticated, proceed with further checks/rendering.
    if (!loading && isAuthenticated) {
        // Check admin role if required for this route
        if (isAdminRoute && currentUser?.role !== 'Administrator') {
            console.warn(`[ProtectedRoute] ADMIN CHECK FAILED! loading=false, isAuthenticated=true, role='${currentUser?.role}'. Redirecting.`);
            alert("Access Denied: Admin privileges required.");
            return <Navigate to="/request-reservation" replace />;
        }

        // If all checks pass (loading done, authenticated, authorized)
        console.log('[ProtectedRoute] Condition: All checks passed. Rendering children or Outlet.');
        return children ? children : <Outlet />;
    }

    // Fallback case - should ideally not be reached if logic above is sound.
    console.error("[ProtectedRoute] Reached unexpected fallback state. Investigate.", { loading, isAuthenticated });
    // Render null or a generic error component might be better than redirecting again.
    return null;
};

export default ProtectedRoute;